import { authClient, useSession } from "@/lib/auth-client";
import { isPostHogEnabled, identifyUser } from "@/lib/posthog";
import { AnalyticsEvents, track } from "@/lib/analytics";
import { useRouterState } from "@tanstack/react-router";
import { PostHogProvider } from "posthog-js/react";
import { posthog } from "@/lib/posthog";
import { useEffect, useRef } from "react";

function IdentitySync() {
  const { data: session } = useSession();
  const { data: organization } = authClient.useActiveOrganization();
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!isPostHogEnabled()) return;
    const user = session?.user;
    if (!user?.id) {
      if (lastId.current) {
        lastId.current = null;
      }
      return;
    }
    const key = `${user.id}:${organization?.id ?? "none"}`;
    if (lastId.current === key) return;
    const isFirstSignIn = lastId.current === null;
    lastId.current = key;
    if (isFirstSignIn) track(AnalyticsEvents.SignedIn);
    identifyUser({
      userId: user.id,
      email: user.email,
      name: user.name,
      organizationId: organization?.id ?? null,
      organizationName: (organization as { name?: string } | null)?.name ?? null,
    });
  }, [session?.user, organization?.id]);

  return null;
}

function PageViewTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.searchStr });

  useEffect(() => {
    if (!isPostHogEnabled()) return;
    track("$pageview", { path: pathname, search });
  }, [pathname, search]);

  return null;
}

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  if (!isPostHogEnabled()) return <>{children}</>;
  return (
    <PostHogProvider client={posthog}>
      <IdentitySync />
      <PageViewTracker />
      {children}
    </PostHogProvider>
  );
}

export { useFeatureFlagVariantKey as useFeatureFlag } from "posthog-js/react";
